<template>
    <pendo-modal
        :visible="visible"
        height="auto"
        max-height="90%"
        width="800px"
        :title="fileName"
        @open="openModal"
        @close="$emit('close')">
        <pendo-alert
            v-if="hasError"
            class="'voc-item-attachment-preview__alert"
            data-cy="'voc-item-attachment-preview-alert"
            type="error"
            title="Something went wrong! Please try again." />
        <div
            ref="imageElement"
            data-cy="voc-item-attachment-preview"
            :class="{ 'voc-item-attachment-preview__wrapper--fullscreen': isFullscreen }">
            <carousel
                ref="imageCarousel"
                :navigation-enabled="true"
                :pagination-enabled="true"
                :loop="true"
                :per-page="1"
                @page-change="carouselPageChanged">
                <slide
                    v-for="image in images"
                    :key="image.url"
                    class="voc-item-attachment-preview__slide"
                    :class="{ 'voc-item-attachment-preview__slide--not-fullscreen': !isFullscreen }">
                    <img
                        v-if="image.loaded"
                        :src="image.url"
                        data-cy="voc-feedback-form-preview-image"
                        class="voc-item-attachment-preview__image"
                        :class="{ 'voc-item-attachment-preview__image--fullscreen': isFullscreen }">
                    <div
                        v-else
                        class="voc-item-attachment-preview__image--loading">
                        <pendo-loading-indicator size="large" />
                    </div>
                </slide>
            </carousel>
            <pendo-button
                v-if="isFullscreen"
                class="voc-item-attachment-preview__fullscreen-close"
                icon="x"
                type="tertiary"
                icon-size="50"
                @click="toggleFullscreen" />

            <div
                v-if="!isFullscreen"
                class="voc-item-attachment-preview__size">
                <span class="voc-item-attachment-preview__size-label">Size: </span> {{ fileSize }}
            </div>
        </div>
        <template #footer>
            <div class="voc-item-attachment-preview__footer">
                <pendo-button
                    v-if="canDelete"
                    :loading="isDeleting"
                    icon="trash-2"
                    type="danger"
                    data-cy="voc-item-attachment-preview-delete"
                    @click="deleteAttachment(fileId)">
                    {{ isBlade ? 'Delete' : 'Remove' }} image
                </pendo-button>
                <span class="voc-item-attachment-preview__space">&nbsp;</span>
                <pendo-button
                    icon="maximize"
                    theme="app"
                    type="secondary"
                    data-cy="voc-item-attachment-preview-fullscreen"
                    @click="toggleFullscreen">
                    Fullscreen
                </pendo-button>
                <pendo-button
                    v-if="isBlade"
                    theme="app"
                    icon="download"
                    type="primary"
                    data-cy="voc-item-attachment-preview-download"
                    @click="downloadAttachment">
                    Download image
                </pendo-button>
            </div>
        </template>
    </pendo-modal>
</template>

<script>
import { PendoModal, PendoTooltip, PendoButton, PendoLoadingIndicator, PendoAlert } from '@pendo/components';
import { getSizeFromBytes } from '@/voc/helpers/common.helper';
import Carousel from '@/common/carousel/carousel.vue';
import Slide from '@/common/carousel/slide.vue';
import { useFullscreen } from '@vueuse/core';
import { ref, unref, computed, watch } from 'vue';

export default {
    components: {
        PendoModal,
        PendoButton,
        PendoLoadingIndicator,
        PendoAlert,
        Carousel,
        Slide,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        blade: {
            type: Boolean,
            default: false,
        },
        canDelete: {
            type: Boolean,
            default: false,
        },
        deleting: {
            type: Boolean,
            default: false,
        },
        imageList: {
            type: Array,
            required: true,
        },
        imageIndex: {
            type: Number,
            default: 0,
        },
        visible: {
            type: Boolean,
            default: false,
        },
        error: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close', 'delete'],
    setup (props, { emit }) {
        const imageElement = ref(null);
        const imageCarousel = ref(null);
        const isBlade = computed(() => props.blade);
        const currentPage = ref(props.imageIndex);
        const { toggle: toggleFullscreen, isFullscreen } = useFullscreen(imageElement);
        const images = ref([]);
        const fileName = computed(() => unref(images)[unref(currentPage)]?.name || 'Unknown');
        const fileSize = computed(() => unref(images)[unref(currentPage)]?.size);
        const fileId = computed(() => props.imageList[unref(currentPage)]?.id);
        const imagesLoaded = computed(() => unref(images).filter((image) => image.loaded).length);
        const isDeleting = computed(() => props.deleting);
        const hasImagesLoaded = computed(() => unref(imagesLoaded) > 0 && unref(imagesLoaded) === unref(images).length);
        const hasError = computed(() => props.error);

        const openModal = () => {
            currentPage.value = props.imageIndex;
            images.value = props.imageList.map((image, imageIndex) => {
                const size = getSizeFromBytes(image.file.size);
                const { name } = image.file;
                const reader = new FileReader();

                reader.onload = () => {
                    images.value[imageIndex].url = reader.result;
                    images.value[imageIndex].loaded = true;
                };
                reader.readAsDataURL(image.file);

                return {
                    size,
                    name,
                    loaded: false,
                    url: null,
                };
            });
        };

        const carouselPageChanged = (page) => {
            currentPage.value = page;
        };

        const downloadAttachment = () => {
            const downloadLink = document.createElement('a');
            downloadLink.href = unref(images)[unref(currentPage)].url;
            downloadLink.download = unref(fileName);
            downloadLink.click();
            downloadLink.remove();
        };

        const deleteAttachment = () => {
            emit('delete', { id: unref(fileId), type: 'image', trigger: 'modal' });
        };

        watch(hasImagesLoaded, (hasLoaded) => {
            if (hasLoaded) {
                unref(imageCarousel).goToPage(unref(currentPage), 'navigation');
            }
        });

        return {
            imageElement,
            imageCarousel,
            isFullscreen,
            isBlade,
            isDeleting,
            hasImagesLoaded,
            hasError,
            currentPage,
            images,
            fileName,
            fileSize,

            toggleFullscreen,
            openModal,
            carouselPageChanged,
            downloadAttachment,
            deleteAttachment,
        };
    },
};
</script>

<style lang="less" scoped>
.voc-item-attachment-preview {
    &__image {
        width: calc(100% - 80px);
        height: 100%;
        object-fit: scale-down;
        margin: 0 40px;
        position: absolute;

        &--loading {
            padding: 50px 0;
        }

        &--fullscreen {
            position: relative;
        }
    }

    &__slide {
        max-width: 100%;

        &--not-fullscreen {
            height: 0;
            padding-bottom: 56%;
        }
    }

    &__size {
        text-align: right;
        padding-top: 24px;

        &-label {
            font-weight: 600;
        }
    }

    &__fullscreen-close {
        position: absolute;
        right: 0;
        top: 0;
        height: 70px;
        color: var(--palette-gray-0);

        &:hover {
            color: var(--palette-gray-70);
        }
    }

    &__space {
        flex-grow: 1;
    }

    &__wrapper--fullscreen {
        display: flex;
        justify-content: center;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-items: stretch;
    }

    &__alert {
        margin: 0 auto 18px;
    }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.voc-item-attachment-preview__wrapper--fullscreen .VueCarousel-inner {
    max-height: 100%;
}
</style>
